// useDashboard — composite hook for the operations dashboard.
//
// There is no /api/v1/dashboard endpoint server-side (UI_AUDIT.md mentions
// one but the route is not registered in app.ts). This hook assembles the
// dashboard from the endpoints that DO exist:
//
//   - /api/v1/collections/bookings        (today's rows, status breakdown, KPI counts)
//   - /api/v1/collections/bookings        (yesterday — for delta)
//   - /api/v1/collections/pending         (open + final-notice counts, two probes)
//   - /api/v1/collections/bookings        (cancellation_requested probe)
//   - /api/v1/activity-log    (recent feed)
//
// Refresh: refetchInterval 60s, staleTime 55s — per Wave B1 brief.
//
// Inventory cards (legacy "Pending Depollution" / "Ready to Scrap") have no
// server endpoint yet — exposed as `inventory: null` in the return value
// and rendered as "—" placeholders by the page (TODO at the call site).

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import type {
  ActivityLogListResponse,
  BookingListResponse,
  BookingStatus,
  BookingSummary,
  PendingListResponse,
} from '../../../../types/api';

const DASHBOARD_REFETCH_MS = 60_000;
const DASHBOARD_STALE_MS = 55_000;
const ACTIVITY_LIMIT = 10;
const TODAY_BOOKINGS_LIMIT = 200;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function previousDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() - 1);
  return isoDate(dt);
}

function percentDelta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 100);
}

export interface DashboardKpi {
  count: number;
  /** Percent vs the same metric on the previous day. `null` when previous=0
   *  and current>0 (delta is undefined for division-by-zero). */
  deltaPercent: number | null;
}

export interface DashboardKpis {
  total: DashboardKpi;
  collected: DashboardKpi;
  paid: DashboardKpi;
  cancelled: DashboardKpi;
}

export interface DashboardStatusEntry {
  status: BookingStatus;
  count: number;
}

export interface DashboardInventory {
  pendingDepollution: number | null;
  readyToScrap: number | null;
}

export interface DashboardData {
  date: string; // today (YYYY-MM-DD, local)
  kpis: DashboardKpis;
  statusBreakdown: DashboardStatusEntry[];
  pendingOpen: number;
  pendingFinalNotice: number;
  cancellationRequests: number;
  inventory: DashboardInventory;
  activity: ActivityLogListResponse['logs'];
  isLoading: boolean;
  isError: boolean;
  lastUpdatedAt: number;
  refetch: () => void;
}

function countBookings(rows: BookingSummary[], pred: (r: BookingSummary) => boolean): number {
  let n = 0;
  for (const r of rows) if (pred(r)) n += 1;
  return n;
}

function statusBreakdown(rows: BookingSummary[]): DashboardStatusEntry[] {
  const order: BookingStatus[] = [
    'pending',
    'in_progress',
    'completed',
    'cancellation_requested',
    'cancelled',
  ];
  const counts = new Map<BookingStatus, number>();
  for (const r of rows) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
  return order
    .map((s) => ({ status: s, count: counts.get(s) ?? 0 }))
    .filter((e) => e.count > 0);
}

interface UseDashboardOptions {
  /** Override "today" — used by tests. Defaults to local today. */
  today?: string;
}

export function useDashboard(options: UseDashboardOptions = {}): DashboardData {
  const today = options.today ?? isoDate(new Date());
  const yesterday = previousDay(today);

  const todayQuery = useQuery({
    queryKey: queryKeys.dashboard.today(today),
    queryFn: async () => {
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
        params: { date_from: today, date_to: today, limit: TODAY_BOOKINGS_LIMIT },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const yesterdayQuery = useQuery({
    queryKey: queryKeys.dashboard.forDate(yesterday),
    queryFn: async () => {
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
        params: { date_from: yesterday, date_to: yesterday, limit: TODAY_BOOKINGS_LIMIT },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const pendingOpenQuery = useQuery({
    queryKey: [...queryKeys.dashboard.pending, 'open'],
    queryFn: async () => {
      const res = await apiClient.get<PendingListResponse>(endpoints.pending.list, {
        params: { status: 'pending', limit: 1 },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const pendingFinalQuery = useQuery({
    queryKey: [...queryKeys.dashboard.pending, 'final_notice'],
    queryFn: async () => {
      const res = await apiClient.get<PendingListResponse>(endpoints.pending.list, {
        params: { status: 'final_notice', limit: 1 },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const cancellationsQuery = useQuery({
    queryKey: queryKeys.dashboard.cancellations,
    queryFn: async () => {
      const res = await apiClient.get<BookingListResponse>(endpoints.bookings.list, {
        params: { status: 'cancellation_requested', limit: 1 },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const activityQuery = useQuery({
    queryKey: queryKeys.dashboard.activity(ACTIVITY_LIMIT),
    queryFn: async () => {
      const res = await apiClient.get<ActivityLogListResponse>(endpoints.activityLog.list, {
        params: { limit: ACTIVITY_LIMIT },
      });
      return res.data;
    },
    staleTime: DASHBOARD_STALE_MS,
    refetchInterval: DASHBOARD_REFETCH_MS,
  });

  const todayRows = todayQuery.data?.bookings ?? [];
  const yesterdayRows = yesterdayQuery.data?.bookings ?? [];

  const totalToday = todayRows.length;
  const collectedToday = countBookings(todayRows, (r) => r.collected);
  const paidToday = countBookings(todayRows, (r) => r.paid);
  const cancelledToday = countBookings(todayRows, (r) => r.status === 'cancelled');

  const totalYesterday = yesterdayRows.length;
  const collectedYesterday = countBookings(yesterdayRows, (r) => r.collected);
  const paidYesterday = countBookings(yesterdayRows, (r) => r.paid);
  const cancelledYesterday = countBookings(yesterdayRows, (r) => r.status === 'cancelled');

  const queries = [
    todayQuery,
    yesterdayQuery,
    pendingOpenQuery,
    pendingFinalQuery,
    cancellationsQuery,
    activityQuery,
  ];
  const isLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const lastUpdatedAt = queries.reduce((acc, q) => Math.max(acc, q.dataUpdatedAt), 0);

  return {
    date: today,
    kpis: {
      total: { count: totalToday, deltaPercent: percentDelta(totalToday, totalYesterday) },
      collected: {
        count: collectedToday,
        deltaPercent: percentDelta(collectedToday, collectedYesterday),
      },
      paid: { count: paidToday, deltaPercent: percentDelta(paidToday, paidYesterday) },
      cancelled: {
        count: cancelledToday,
        deltaPercent: percentDelta(cancelledToday, cancelledYesterday),
      },
    },
    statusBreakdown: statusBreakdown(todayRows),
    pendingOpen: pendingOpenQuery.data?.pagination.total ?? 0,
    pendingFinalNotice: pendingFinalQuery.data?.pagination.total ?? 0,
    cancellationRequests: cancellationsQuery.data?.pagination.total ?? 0,
    // TODO: server endpoints for "Pending Depollution" and "Ready to Scrap"
    //   inventory counts (UI_AUDIT.md §2.1 Section B) do not exist yet.
    //   Track in HANDOFF gaps; render placeholders in the page until added.
    inventory: { pendingDepollution: null, readyToScrap: null },
    activity: activityQuery.data?.logs ?? [],
    isLoading,
    isError,
    lastUpdatedAt,
    refetch: () => {
      for (const q of queries) void q.refetch();
    },
  };
}
